import React, {
  createContext, useContext, useState, useEffect, useCallback, useRef, useMemo,
} from 'react';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db, CAMPAIGN_ID } from '../firebase';
import { GRID_W as DEFAULT_W, GRID_H as DEFAULT_H } from '../components/canvas/GridLayer';
import {
  encodeTerrain, decodeTerrain,
  encodeFog, decodeFog,
  encodeElevation, decodeElevation,
} from '../lib/mapCodec';

// Re-export terrain definitions from shared module (avoids circular import with mapCodec)
export { TERRAIN_TYPES, TERRAIN_MAP } from '../lib/terrain';
import { TERRAIN_MAP } from '../lib/terrain';

const MapContext = createContext(null);

/** Convert grid coords to flat array index. Pass gridW when working with a dynamic grid. */
export function cellIdx(x, y, w = DEFAULT_W) { return y * w + x; }

function makeEmptyArrays(w, h) {
  const len = w * h;
  return {
    terrain:   new Array(len).fill('empty'),
    fog:       new Array(len).fill(false),
    elevation: new Array(len).fill(0),
  };
}

function floodFillTerrain(terrainArr, startX, startY, targetTerrain, newTerrain, gridW, gridH) {
  if (targetTerrain === newTerrain) return terrainArr;
  const next    = [...terrainArr];
  const queue   = [[startX, startY]];
  const visited = new Set([cellIdx(startX, startY, gridW)]);
  while (queue.length) {
    const [cx, cy] = queue.shift();
    const idx = cellIdx(cx, cy, gridW);
    next[idx] = newTerrain;
    for (const [nx, ny] of [[cx-1,cy],[cx+1,cy],[cx,cy-1],[cx,cy+1]]) {
      if (nx < 0 || nx >= gridW || ny < 0 || ny >= gridH) continue;
      const nIdx = cellIdx(nx, ny, gridW);
      if (!visited.has(nIdx) && terrainArr[nIdx] === targetTerrain) {
        visited.add(nIdx);
        queue.push([nx, ny]);
      }
    }
  }
  return next;
}

const MAP_DOC_ID = 'main';
const MAX_UNDO   = 50;

export function MapProvider({ children }) {
  const [terrain,   setTerrain]   = useState(() => new Array(DEFAULT_W * DEFAULT_H).fill('empty'));
  const [fog,       setFog]       = useState(() => new Array(DEFAULT_W * DEFAULT_H).fill(false));
  const [elevation, setElevation] = useState(() => new Array(DEFAULT_W * DEFAULT_H).fill(0));
  const [gridW,      setGridW]      = useState(DEFAULT_W);
  const [gridH,      setGridH]      = useState(DEFAULT_H);
  const [spawnPoint, setSpawnPointState] = useState({ x: 1, y: 1 });
  const [mapName,    setMapName]    = useState('Main Map');
  const [ambient,    setAmbientState] = useState(null); // null | { preset, color, alpha }
  const [mapLoaded,  setMapLoaded]  = useState(false);
  const [walls,      setWalls]      = useState([]);
  const [doors,      setDoors]      = useState([]);
  const [paths,      setPaths]      = useState([]);

  // ── Undo/redo — typed entries ────────────────────────────────────────────
  const undoStack = useRef([]);
  const redoStack = useRef([]);

  const terrainSaveTimer   = useRef(null);
  const fogSaveTimer       = useRef(null);
  const elevationSaveTimer = useRef(null);
  const wallSaveTimer      = useRef(null);
  const doorSaveTimer      = useRef(null);
  const pathSaveTimer      = useRef(null);

  // Keep refs so callbacks can always read current values without stale closures
  const gridWRef     = useRef(gridW);
  const gridHRef     = useRef(gridH);
  const terrainRef   = useRef(terrain);
  const fogRef       = useRef(fog);
  const elevationRef = useRef(elevation);
  const wallsRef     = useRef(walls);
  const doorsRef     = useRef(doors);
  const pathsRef     = useRef(paths);
  useEffect(() => { gridWRef.current = gridW; }, [gridW]);
  useEffect(() => { gridHRef.current = gridH; }, [gridH]);
  useEffect(() => { terrainRef.current = terrain; }, [terrain]);
  useEffect(() => { fogRef.current = fog; }, [fog]);
  useEffect(() => { elevationRef.current = elevation; }, [elevation]);
  useEffect(() => { wallsRef.current = walls; }, [walls]);
  useEffect(() => { doorsRef.current = doors; }, [doors]);
  useEffect(() => { pathsRef.current = paths; }, [paths]);

  // ── Backward-compat derived cells (useMemo) ─────────────────────────────
  const cells = useMemo(() => {
    return terrain.map((t, i) => {
      const def = TERRAIN_MAP[t] || TERRAIN_MAP.empty;
      return {
        terrain: t,
        passable: def.passable,
        difficult: !!def.difficult,
        swim: !!def.swim,
        climb: !!def.climb,
        fogRevealed: fog[i] || false,
        elevation: elevation[i] || 0,
      };
    });
  }, [terrain, fog, elevation]);

  // ── Firestore snapshot ─────────────────────────────────────────────────────
  useEffect(() => {
    const mapRef = doc(db, 'campaigns', CAMPAIGN_ID, 'maps', MAP_DOC_ID);
    const unsub  = onSnapshot(mapRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        const w = data.gridW || DEFAULT_W;
        const h = data.gridH || DEFAULT_H;
        setGridW(w);
        setGridH(h);
        gridWRef.current = w;
        gridHRef.current = h;
        if (data.spawnPoint) setSpawnPointState(data.spawnPoint);
        if (data.name) setMapName(data.name);
        if (data.ambient !== undefined) setAmbientState(data.ambient);

        const len = w * h;

        // New binary format
        if (typeof data.terrain === 'string') {
          setTerrain(decodeTerrain(data.terrain, len));
          setFog(data.fog ? decodeFog(data.fog, len) : new Array(len).fill(false));
          setElevation(data.elevation ? decodeElevation(data.elevation, len) : new Array(len).fill(0));
        }
        // Legacy cells[] format
        else if (data.cells && data.cells.length === len) {
          setTerrain(data.cells.map((c) => c.terrain || 'empty'));
          setFog(data.cells.map((c) => c.fogRevealed || false));
          setElevation(data.cells.map((c) => c.elevation || 0));
        }

        setWalls(data.walls || []);
        setDoors(data.doors || []);
        setPaths(data.paths || []);
      } else {
        const empty = makeEmptyArrays(DEFAULT_W, DEFAULT_H);
        setDoc(mapRef, {
          name: 'Main Map', theme: 'dungeon',
          gridW: DEFAULT_W, gridH: DEFAULT_H,
          spawnPoint: { x: 1, y: 1 },
          terrain: encodeTerrain(empty.terrain),
          fog: encodeFog(empty.fog),
          elevation: encodeElevation(empty.elevation),
          created: Date.now(),
        });
        setTerrain(empty.terrain);
        setFog(empty.fog);
        setElevation(empty.elevation);
      }
      setMapLoaded(true);
    });
    return () => {
      unsub();
      // Clear any pending save timers so orphaned writes don't fire after unmount
      if (terrainSaveTimer.current)   { clearTimeout(terrainSaveTimer.current);   terrainSaveTimer.current = null; }
      if (fogSaveTimer.current)       { clearTimeout(fogSaveTimer.current);       fogSaveTimer.current = null; }
      if (elevationSaveTimer.current) { clearTimeout(elevationSaveTimer.current); elevationSaveTimer.current = null; }
      if (wallSaveTimer.current)      { clearTimeout(wallSaveTimer.current);      wallSaveTimer.current = null; }
      if (doorSaveTimer.current)      { clearTimeout(doorSaveTimer.current);      doorSaveTimer.current = null; }
      if (pathSaveTimer.current)      { clearTimeout(pathSaveTimer.current);      pathSaveTimer.current = null; }
      if (fogRevealTimer.current)     { clearTimeout(fogRevealTimer.current);     fogRevealTimer.current = null; fogRevealQueue.current = null; }
    };
  }, []);

  // ── Debounced saves (one per layer) ────────────────────────────────────────
  const scheduleSaveTerrain = useCallback(() => {
    if (terrainSaveTimer.current) clearTimeout(terrainSaveTimer.current);
    terrainSaveTimer.current = setTimeout(async () => {
      const mapRef = doc(db, 'campaigns', CAMPAIGN_ID, 'maps', MAP_DOC_ID);
      await setDoc(mapRef, { terrain: encodeTerrain(terrainRef.current) }, { merge: true });
    }, 600);
  }, []);

  const scheduleSaveFog = useCallback(() => {
    if (fogSaveTimer.current) clearTimeout(fogSaveTimer.current);
    fogSaveTimer.current = setTimeout(async () => {
      const mapRef = doc(db, 'campaigns', CAMPAIGN_ID, 'maps', MAP_DOC_ID);
      await setDoc(mapRef, { fog: encodeFog(fogRef.current) }, { merge: true });
    }, 600);
  }, []);

  const scheduleSaveElevation = useCallback(() => {
    if (elevationSaveTimer.current) clearTimeout(elevationSaveTimer.current);
    elevationSaveTimer.current = setTimeout(async () => {
      const mapRef = doc(db, 'campaigns', CAMPAIGN_ID, 'maps', MAP_DOC_ID);
      await setDoc(mapRef, { elevation: encodeElevation(elevationRef.current) }, { merge: true });
    }, 600);
  }, []);

  const scheduleSaveWalls = useCallback(() => {
    if (wallSaveTimer.current) clearTimeout(wallSaveTimer.current);
    wallSaveTimer.current = setTimeout(async () => {
      const mapRef = doc(db, 'campaigns', CAMPAIGN_ID, 'maps', MAP_DOC_ID);
      await setDoc(mapRef, { walls: wallsRef.current }, { merge: true });
    }, 600);
  }, []);

  const scheduleSaveDoors = useCallback(() => {
    if (doorSaveTimer.current) clearTimeout(doorSaveTimer.current);
    doorSaveTimer.current = setTimeout(async () => {
      const mapRef = doc(db, 'campaigns', CAMPAIGN_ID, 'maps', MAP_DOC_ID);
      await setDoc(mapRef, { doors: doorsRef.current }, { merge: true });
    }, 600);
  }, []);

  const scheduleSavePaths = useCallback(() => {
    if (pathSaveTimer.current) clearTimeout(pathSaveTimer.current);
    pathSaveTimer.current = setTimeout(async () => {
      const mapRef = doc(db, 'campaigns', CAMPAIGN_ID, 'maps', MAP_DOC_ID);
      await setDoc(mapRef, { paths: pathsRef.current }, { merge: true });
    }, 600);
  }, []);

  // ── Unified undo/redo ──────────────────────────────────────────────────────
  const pushUndo = useCallback((type, prev, next) => {
    undoStack.current.push({ type, prev, next });
    if (undoStack.current.length > MAX_UNDO) undoStack.current.shift();
    redoStack.current = [];
  }, []);

  const applyUndoEntry = useCallback((entry, direction) => {
    if (entry.type === 'composite') {
      entry.entries.forEach((e) => applyUndoEntry(e, direction));
      return;
    }
    const data = entry[direction];
    switch (entry.type) {
      case 'terrain':   setTerrain(data); terrainRef.current = data; scheduleSaveTerrain(); break;
      case 'elevation': setElevation(data); elevationRef.current = data; scheduleSaveElevation(); break;
      case 'walls':     setWalls(data); wallsRef.current = data; scheduleSaveWalls(); break;
      case 'doors':     setDoors(data); doorsRef.current = data; scheduleSaveDoors(); break;
      case 'paths':     setPaths(data); pathsRef.current = data; scheduleSavePaths(); break;
    }
  }, [scheduleSaveTerrain, scheduleSaveElevation, scheduleSaveWalls, scheduleSaveDoors, scheduleSavePaths]);

  const undo = useCallback(() => {
    const entry = undoStack.current.pop();
    if (!entry) return;
    applyUndoEntry(entry, 'prev');
    redoStack.current.push(entry);
  }, [applyUndoEntry]);

  const redo = useCallback(() => {
    const entry = redoStack.current.pop();
    if (!entry) return;
    applyUndoEntry(entry, 'next');
    undoStack.current.push(entry);
  }, [applyUndoEntry]);

  const canUndo = undoStack.current.length > 0;
  const canRedo = redoStack.current.length > 0;

  // ── Wall CRUD ──────────────────────────────────────────────────────────────
  const addWall = useCallback((wall) => {
    const prev = wallsRef.current;
    const next = [...prev, wall];
    setWalls(next); wallsRef.current = next;
    scheduleSaveWalls();
    pushUndo('walls', prev, next);
  }, [scheduleSaveWalls, pushUndo]);

  const updateWall = useCallback((id, patch) => {
    const prev = wallsRef.current;
    const next = prev.map((w) => w.id === id ? { ...w, ...patch } : w);
    setWalls(next); wallsRef.current = next;
    scheduleSaveWalls();
    pushUndo('walls', prev, next);
  }, [scheduleSaveWalls, pushUndo]);

  const deleteWall = useCallback((id) => {
    const prev = wallsRef.current;
    const next = prev.filter((w) => w.id !== id);
    setWalls(next); wallsRef.current = next;
    scheduleSaveWalls();
    pushUndo('walls', prev, next);
  }, [scheduleSaveWalls, pushUndo]);

  const removeWallSegment = useCallback((wallId, segIdx) => {
    const prev = wallsRef.current;
    const idx = prev.findIndex((w) => w.id === wallId);
    if (idx === -1) return;
    const wall = prev[idx];
    const pts  = wall.pts;
    if (!pts || pts.length < 2) return;

    const next = [...prev];
    const segCount = wall.closed ? pts.length : pts.length - 1;
    if (segIdx < 0 || segIdx >= segCount) return;

    if (segCount === 1) {
      next.splice(idx, 1);
    } else if (wall.closed) {
      const reordered = [];
      for (let i = 1; i <= pts.length; i++)
        reordered.push(pts[(segIdx + i) % pts.length]);
      next[idx] = { ...wall, pts: reordered, closed: false };
    } else if (segIdx === 0) {
      next[idx] = { ...wall, pts: pts.slice(1) };
    } else if (segIdx === pts.length - 2) {
      next[idx] = { ...wall, pts: pts.slice(0, -1) };
    } else {
      const wallA = { ...wall, id: wall.id + '_a', pts: pts.slice(0, segIdx + 1) };
      const wallB = { ...wall, id: wall.id + '_b', pts: pts.slice(segIdx + 1) };
      next.splice(idx, 1, wallA, wallB);
    }
    setWalls(next); wallsRef.current = next;
    scheduleSaveWalls();
    pushUndo('walls', prev, next);
  }, [scheduleSaveWalls, pushUndo]);

  const clearWalls = useCallback(() => {
    const prev = wallsRef.current;
    setWalls([]); wallsRef.current = [];
    scheduleSaveWalls();
    if (prev.length > 0) pushUndo('walls', prev, []);
  }, [scheduleSaveWalls, pushUndo]);

  // ── Path CRUD (decorative; mirrors wall CRUD shape) ───────────────────────
  const addPath = useCallback((path) => {
    const prev = pathsRef.current;
    const next = [...prev, path];
    setPaths(next); pathsRef.current = next;
    scheduleSavePaths();
    pushUndo('paths', prev, next);
  }, [scheduleSavePaths, pushUndo]);

  const updatePath = useCallback((id, patch) => {
    const prev = pathsRef.current;
    const next = prev.map((p) => p.id === id ? { ...p, ...patch } : p);
    setPaths(next); pathsRef.current = next;
    scheduleSavePaths();
    pushUndo('paths', prev, next);
  }, [scheduleSavePaths, pushUndo]);

  const deletePath = useCallback((id) => {
    const prev = pathsRef.current;
    const next = prev.filter((p) => p.id !== id);
    setPaths(next); pathsRef.current = next;
    scheduleSavePaths();
    pushUndo('paths', prev, next);
  }, [scheduleSavePaths, pushUndo]);

  const removePathSegment = useCallback((pathId, segIdx) => {
    const prev = pathsRef.current;
    const idx = prev.findIndex((p) => p.id === pathId);
    if (idx === -1) return;
    const path = prev[idx];
    const pts  = path.pts;
    if (!pts || pts.length < 2) return;

    const next = [...prev];
    const segCount = pts.length - 1;
    if (segIdx < 0 || segIdx >= segCount) return;

    if (segCount === 1) {
      next.splice(idx, 1);
    } else if (segIdx === 0) {
      next[idx] = { ...path, pts: pts.slice(1) };
    } else if (segIdx === pts.length - 2) {
      next[idx] = { ...path, pts: pts.slice(0, -1) };
    } else {
      const a = { ...path, id: path.id + '_a', pts: pts.slice(0, segIdx + 1) };
      const b = { ...path, id: path.id + '_b', pts: pts.slice(segIdx + 1) };
      next.splice(idx, 1, a, b);
    }
    setPaths(next); pathsRef.current = next;
    scheduleSavePaths();
    pushUndo('paths', prev, next);
  }, [scheduleSavePaths, pushUndo]);

  const clearPaths = useCallback(() => {
    const prev = pathsRef.current;
    setPaths([]); pathsRef.current = [];
    scheduleSavePaths();
    if (prev.length > 0) pushUndo('paths', prev, []);
  }, [scheduleSavePaths, pushUndo]);

  // ── Door CRUD ──────────────────────────────────────────────────────────────
  const addDoor = useCallback((door) => {
    const prev = doorsRef.current;
    const next = [...prev, door];
    setDoors(next); doorsRef.current = next;
    scheduleSaveDoors();
    pushUndo('doors', prev, next);
  }, [scheduleSaveDoors, pushUndo]);

  const deleteDoor = useCallback((id) => {
    const prev = doorsRef.current;
    const next = prev.filter((d) => d.id !== id);
    setDoors(next); doorsRef.current = next;
    scheduleSaveDoors();
    pushUndo('doors', prev, next);
  }, [scheduleSaveDoors, pushUndo]);

  const updateDoor = useCallback((id, patch) => {
    const prev = doorsRef.current;
    const idx = prev.findIndex((d) => d.id === id);
    if (idx === -1) return;
    const next = [...prev];
    next[idx] = { ...next[idx], ...patch };
    setDoors(next); doorsRef.current = next;
    scheduleSaveDoors();
    pushUndo('doors', prev, next);
  }, [scheduleSaveDoors, pushUndo]);

  // ── Terrain painting ───────────────────────────────────────────────────────
  const paintCell = useCallback((x, y, newTerrain, { pushToUndo = false } = {}) => {
    const gW = gridWRef.current; const gH = gridHRef.current;
    if (x < 0 || x >= gW || y < 0 || y >= gH) return;
    setTerrain((prev) => {
      const idx = cellIdx(x, y, gW);
      if (prev[idx] === newTerrain) return prev;
      if (pushToUndo) pushUndo('terrain', prev, null); // next patched below
      const next = [...prev];
      next[idx] = newTerrain;
      terrainRef.current = next;
      scheduleSaveTerrain();
      // Patch the undo entry's next now that we know it
      if (pushToUndo && undoStack.current.length > 0) {
        undoStack.current[undoStack.current.length - 1].next = next;
      }
      return next;
    });
  }, [pushUndo, scheduleSaveTerrain]);

  const paintCells = useCallback((coords, newTerrain) => {
    if (!coords.length) return;
    const gW = gridWRef.current; const gH = gridHRef.current;
    setTerrain((prev) => {
      const next = [...prev];
      for (const [x, y] of coords) {
        if (x < 0 || x >= gW || y < 0 || y >= gH) continue;
        next[cellIdx(x, y, gW)] = newTerrain;
      }
      terrainRef.current = next;
      scheduleSaveTerrain();
      pushUndo('terrain', prev, next);
      return next;
    });
  }, [pushUndo, scheduleSaveTerrain]);

  const paintElevations = useCallback((coords, newElev) => {
    if (!coords.length) return;
    const gW = gridWRef.current; const gH = gridHRef.current;
    setElevation((prev) => {
      const next = [...prev];
      for (const [x, y] of coords) {
        if (x < 0 || x >= gW || y < 0 || y >= gH) continue;
        next[cellIdx(x, y, gW)] = newElev;
      }
      elevationRef.current = next;
      scheduleSaveElevation();
      pushUndo('elevation', prev, next);
      return next;
    });
  }, [pushUndo, scheduleSaveElevation]);

  const fillAt = useCallback((x, y, newTerrain) => {
    const gW = gridWRef.current; const gH = gridHRef.current;
    if (x < 0 || x >= gW || y < 0 || y >= gH) return;
    setTerrain((prev) => {
      const targetTerrain = prev[cellIdx(x, y, gW)];
      const next = floodFillTerrain(prev, x, y, targetTerrain, newTerrain, gW, gH);
      terrainRef.current = next;
      scheduleSaveTerrain();
      pushUndo('terrain', prev, next);
      return next;
    });
  }, [pushUndo, scheduleSaveTerrain]);

  // ── Grid resize ────────────────────────────────────────────────────────────
  const resizeGrid = useCallback((newW, newH) => {
    const oldW = gridWRef.current;
    const oldH = gridHRef.current;
    const prevTerrain = terrainRef.current;
    const prevElev    = elevationRef.current;

    const newTerrain = new Array(newW * newH).fill('empty');
    const newFog     = new Array(newW * newH).fill(false);
    const newElev    = new Array(newW * newH).fill(0);
    for (let y = 0; y < Math.min(oldH, newH); y++) {
      for (let x = 0; x < Math.min(oldW, newW); x++) {
        const oldIdx = cellIdx(x, y, oldW);
        const newIdx = cellIdx(x, y, newW);
        newTerrain[newIdx] = prevTerrain[oldIdx] || 'empty';
        newFog[newIdx]     = fogRef.current[oldIdx] || false;
        newElev[newIdx]    = prevElev[oldIdx] || 0;
      }
    }
    setTerrain(newTerrain); terrainRef.current = newTerrain;
    setFog(newFog);         fogRef.current = newFog;
    setElevation(newElev);  elevationRef.current = newElev;
    setGridW(newW); setGridH(newH);
    gridWRef.current = newW; gridHRef.current = newH;

    // Immediate write — no debounce for resize
    const mapRef = doc(db, 'campaigns', CAMPAIGN_ID, 'maps', MAP_DOC_ID);
    setDoc(mapRef, {
      terrain: encodeTerrain(newTerrain),
      fog: encodeFog(newFog),
      elevation: encodeElevation(newElev),
      gridW: newW, gridH: newH,
    }, { merge: true });

    pushUndo('composite', null, null);
    // Replace last entry with proper composite
    undoStack.current[undoStack.current.length - 1] = {
      type: 'composite',
      entries: [
        { type: 'terrain', prev: prevTerrain, next: newTerrain },
        { type: 'elevation', prev: prevElev, next: newElev },
      ],
    };
  }, [pushUndo]);

  // ── Spawn point ────────────────────────────────────────────────────────────
  const setSpawnPoint = useCallback((x, y) => {
    const pt = { x, y };
    setSpawnPointState(pt);
    const mapRef = doc(db, 'campaigns', CAMPAIGN_ID, 'maps', MAP_DOC_ID);
    setDoc(mapRef, { spawnPoint: pt }, { merge: true });
  }, []);

  // ── Ambient lighting (day/night tint) ────────────────────────────────────
  // ambient: null | { preset, color, alpha }
  const setAmbient = useCallback((next) => {
    setAmbientState(next);
    const mapRef = doc(db, 'campaigns', CAMPAIGN_ID, 'maps', MAP_DOC_ID);
    setDoc(mapRef, { ambient: next }, { merge: true });
  }, []);

  // ── Clear map ──────────────────────────────────────────────────────────────
  const clearMap = useCallback(() => {
    const gW = gridWRef.current;
    const gH = gridHRef.current;
    const prevTerrain = terrainRef.current;
    const prevElev    = elevationRef.current;
    const prevWalls   = wallsRef.current;
    const prevDoors   = doorsRef.current;

    const empty = makeEmptyArrays(gW, gH);
    setTerrain(empty.terrain);   terrainRef.current = empty.terrain;
    setFog(empty.fog);           fogRef.current = empty.fog;
    setElevation(empty.elevation); elevationRef.current = empty.elevation;
    setWalls([]);  wallsRef.current = [];
    setDoors([]);  doorsRef.current = [];

    const mapRef = doc(db, 'campaigns', CAMPAIGN_ID, 'maps', MAP_DOC_ID);
    setDoc(mapRef, {
      terrain: encodeTerrain(empty.terrain),
      fog: encodeFog(empty.fog),
      elevation: encodeElevation(empty.elevation),
      walls: [], doors: [],
    }, { merge: true });

    // Composite undo entry
    const entries = [];
    if (prevTerrain.some((t) => t !== 'empty')) entries.push({ type: 'terrain', prev: prevTerrain, next: empty.terrain });
    if (prevElev.some((e) => e !== 0)) entries.push({ type: 'elevation', prev: prevElev, next: empty.elevation });
    if (prevWalls.length > 0) entries.push({ type: 'walls', prev: prevWalls, next: [] });
    if (prevDoors.length > 0) entries.push({ type: 'doors', prev: prevDoors, next: [] });
    if (entries.length > 0) {
      undoStack.current.push({ type: 'composite', entries });
      if (undoStack.current.length > MAX_UNDO) undoStack.current.shift();
      redoStack.current = [];
    }
  }, []);

  // ── Fog ────────────────────────────────────────────────────────────────────
  const paintFogCells = useCallback((coords, revealed) => {
    if (!coords.length) return;
    const gW = gridWRef.current; const gH = gridHRef.current;
    setFog((prev) => {
      const next = [...prev]; let changed = false;
      for (const [x, y] of coords) {
        if (x < 0 || x >= gW || y < 0 || y >= gH) continue;
        const idx = cellIdx(x, y, gW);
        if (prev[idx] !== revealed) { next[idx] = revealed; changed = true; }
      }
      if (!changed) return prev;
      fogRef.current = next;
      scheduleSaveFog();
      return next;
    });
  }, [scheduleSaveFog]);

  // Token-triggered reveals: multiple tokens can drop fog-of-war reveals in
  // quick succession. Collect them in a ref and flush once per animation frame
  // so we do a single setFog / Firestore save instead of one per token.
  const fogRevealQueue = useRef(null);
  const fogRevealTimer = useRef(null);
  const queueFogReveal = useCallback((coords) => {
    if (!coords || !coords.length) return;
    if (!fogRevealQueue.current) fogRevealQueue.current = [];
    for (const c of coords) fogRevealQueue.current.push(c);
    if (fogRevealTimer.current) return;
    fogRevealTimer.current = setTimeout(() => {
      const pending = fogRevealQueue.current || [];
      fogRevealQueue.current = null;
      fogRevealTimer.current = null;
      if (pending.length) paintFogCells(pending, true);
    }, 100);
  }, [paintFogCells]);

  const revealAll = useCallback(() => {
    setFog((prev) => {
      const next = prev.map(() => true);
      fogRef.current = next;
      scheduleSaveFog();
      return next;
    });
  }, [scheduleSaveFog]);

  const hideAll = useCallback(() => {
    setFog((prev) => {
      const next = prev.map(() => false);
      fogRef.current = next;
      scheduleSaveFog();
      return next;
    });
  }, [scheduleSaveFog]);

  // ── Load cells from a saved map ────────────────────────────────────────────
  const loadCells = useCallback(async (newCells, w, h) => {
    const gW = w || gridWRef.current;
    const gH = h || gridHRef.current;
    if (!Array.isArray(newCells) || newCells.length !== gW * gH) return;
    // Accept legacy cells[] format
    const newTerrain = newCells.map((c) => (typeof c === 'string' ? c : c.terrain) || 'empty');
    const newFog     = newCells.map((c) => (typeof c === 'object' ? c.fogRevealed : false) || false);
    const newElev    = newCells.map((c) => (typeof c === 'object' ? c.elevation : 0) || 0);
    setTerrain(newTerrain); terrainRef.current = newTerrain;
    setFog(newFog);         fogRef.current = newFog;
    setElevation(newElev);  elevationRef.current = newElev;
    scheduleSaveTerrain();
    scheduleSaveFog();
    scheduleSaveElevation();
  }, [scheduleSaveTerrain, scheduleSaveFog, scheduleSaveElevation]);

  /** Load a full map snapshot (cells + walls + doors) in one write. */
  const loadFullMap = useCallback(async (mapData) => {
    const gW = mapData.gridW || gridWRef.current;
    const gH = mapData.gridH || gridHRef.current;
    const len = gW * gH;

    let newTerrain, newFog, newElev;

    // Support new packed format
    if (typeof mapData.terrain === 'string') {
      newTerrain = decodeTerrain(mapData.terrain, len);
      newFog     = mapData.fog ? decodeFog(mapData.fog, len) : new Array(len).fill(false);
      newElev    = mapData.elevation ? decodeElevation(mapData.elevation, len) : new Array(len).fill(0);
    }
    // Support legacy cells[] format
    else if (Array.isArray(mapData.cells) && mapData.cells.length === len) {
      newTerrain = mapData.cells.map((c) => (typeof c === 'string' ? c : c.terrain) || 'empty');
      newFog     = mapData.cells.map((c) => (typeof c === 'object' ? c.fogRevealed : false) || false);
      newElev    = mapData.cells.map((c) => (typeof c === 'object' ? c.elevation : 0) || 0);
    } else {
      return; // invalid
    }

    setGridW(gW); setGridH(gH);
    gridWRef.current = gW; gridHRef.current = gH;
    setTerrain(newTerrain); terrainRef.current = newTerrain;
    setFog(newFog);         fogRef.current = newFog;
    setElevation(newElev);  elevationRef.current = newElev;
    setWalls(mapData.walls || []); wallsRef.current = mapData.walls || [];
    setDoors(mapData.doors || []); doorsRef.current = mapData.doors || [];
    undoStack.current = [];
    redoStack.current = [];
    if (mapData.name) setMapName(mapData.name);

    const mapRef = doc(db, 'campaigns', CAMPAIGN_ID, 'maps', MAP_DOC_ID);
    await setDoc(mapRef, {
      terrain: encodeTerrain(newTerrain),
      fog: encodeFog(newFog),
      elevation: encodeElevation(newElev),
      gridW: gW, gridH: gH,
      walls: mapData.walls || [],
      doors: mapData.doors || [],
      ...(mapData.name ? { name: mapData.name } : {}),
    }, { merge: true });
  }, []);

  // ── Keyboard shortcuts ─────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (e.ctrlKey && !e.shiftKey && e.key === 'z') { e.preventDefault(); undo(); }
      if (e.ctrlKey &&  e.shiftKey && e.key === 'Z') { e.preventDefault(); redo(); }
      if (e.ctrlKey &&  e.key === 'y')               { e.preventDefault(); redo(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [undo, redo]);

  return (
    <MapContext.Provider value={{
      cells, terrain, fog, elevation, mapLoaded, mapName,
      gridW, gridH,
      spawnPoint, setSpawnPoint,
      ambient, setAmbient,
      paintCell, paintCells, paintElevations, fillAt,
      resizeGrid, clearMap,
      paintFogCells, queueFogReveal, revealAll, hideAll,
      loadCells, loadFullMap,
      undo, redo, canUndo, canRedo,
      walls, addWall, updateWall, deleteWall, removeWallSegment, clearWalls,
      doors, addDoor, deleteDoor, updateDoor,
      paths, addPath, updatePath, deletePath, removePathSegment, clearPaths,
    }}>
      {children}
    </MapContext.Provider>
  );
}

export function useMap() {
  const ctx = useContext(MapContext);
  if (!ctx) throw new Error('useMap must be inside MapProvider');
  return ctx;
}
